import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { StoreSettingsProvider } from './context/StoreSettingsContext';
import { ProductsProvider, useProducts } from './context/ProductsContext';
import { CartProvider, useCart } from './context/CartContext';
import { ReviewsProvider } from './context/ReviewsContext';
import { HeroProvider } from './context/HeroContext';
import { AnnouncementBar } from './components/layout/AnnouncementBar';
import { Header } from './components/layout/Header';
import { PromoTrustBar } from './components/layout/PromoTrustBar';
import { Footer } from './components/layout/Footer';
import { WhatsAppButton } from './components/layout/WhatsAppButton';
import { MobileMenu } from './components/layout/MobileMenu';
import { CartDrawer } from './components/cart/CartDrawer';

// Critical initial page loaded directly
import { HomePage } from './pages/HomePage';

// Code-split pages (lazy loaded on demand to minimize initial JS bundle)
const ShopAllPage = lazy(() => import('./pages/ShopAllPage').then((m) => ({ default: m.ShopAllPage })));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage').then((m) => ({ default: m.ProductDetailPage })));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage').then((m) => ({ default: m.CheckoutPage })));
const TrackOrderPage = lazy(() => import('./pages/TrackOrderPage').then((m) => ({ default: m.TrackOrderPage })));
const BlogPage = lazy(() => import('./pages/BlogPage').then((m) => ({ default: m.BlogPage })));
const GenericCollectionPage = lazy(() => import('./pages/GenericCollectionPage').then((m) => ({ default: m.GenericCollectionPage })));
const AdminPage = lazy(() => import('./pages/AdminPage').then((m) => ({ default: m.AdminPage })));

// Code-split modals
const SearchModal = lazy(() => import('./components/common/SearchModal').then((m) => ({ default: m.SearchModal })));
const AccountModal = lazy(() => import('./components/common/AccountModal').then((m) => ({ default: m.AccountModal })));
const PolicyModal = lazy(() => import('./components/common/PolicyModal').then((m) => ({ default: m.PolicyModal })));
const QuickOrderModal = lazy(() => import('./components/common/QuickOrderModal').then((m) => ({ default: m.QuickOrderModal })));

import { Product } from './types/store';
import {
  captureAndStoreMarketingAttribution,
  trackPageView,
  initMetaPixel,
  initTikTokPixel,
} from './lib/analytics';
import { useVisitorTracker } from './lib/visitorTracking';
import { PRODUCTS as FALLBACK_PRODUCTS } from './data/products';
import { findProductBySlugOrParam, getProductSlug } from './lib/urlHelpers';
import { updatePageSeo } from './lib/seo';

type PageRoute =
  | 'home'
  | 'shop-all'
  | 'supplements'
  | 'bundles'
  | 'product-detail'
  | 'checkout'
  | 'track-order'
  | 'blog'
  | 'skincare'
  | 'haircare'
  | 'education-toys'
  | 'admin';

const MainAppContent: React.FC = () => {
  const { products, isLoading: isProductsLoading } = useProducts();
  const [currentPage, setCurrentPage] = useState<PageRoute>('home');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [quickOrderProduct, setQuickOrderProduct] = useState<Product | null>(null);
  const [isProductNotFound, setIsProductNotFound] = useState(false);
  const [trackOrderNumber, setTrackOrderNumber] = useState<string>('');

  // Modals
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activePolicy, setActivePolicy] = useState<string | null>(null);

  const { openCart, closeCart } = useCart();

  // Active Live Visitors Real-time Tracking Heartbeat
  useVisitorTracker(currentPage, selectedProduct?.title);

  // Capture UTM parameters and initialize Pixels (Meta & TikTok DA86HQBC77UES9745BGG)
  useEffect(() => {
    captureAndStoreMarketingAttribution();
    initMetaPixel();
    initTikTokPixel();
  }, []);

  // Parse direct URL path and query parameters (e.g. /products/malrich-vanilla or /checkout)
  const resolveRouteFromUrl = useCallback(() => {
    if (typeof window === 'undefined') return;

    const rawPath = window.location.pathname.toLowerCase();
    const hash = window.location.hash ? window.location.hash.replace(/^#/, '').toLowerCase() : '';
    // Handle both direct pathname and hash-based routing fallbacks if present
    const path = (rawPath === '/' || rawPath === '') && hash ? hash.split('?')[0] : rawPath;

    const searchParams = new URLSearchParams(window.location.search);
    const orderParam = searchParams.get('order') || searchParams.get('track');

    if (orderParam) {
      setTrackOrderNumber(orderParam);
      setCurrentPage('track-order');
      return;
    }

    if (path === '/admin' || path === '/admin/' || path === '/admin/login') {
      setCurrentPage('admin');
      return;
    }

    if (path === '/checkout' || path === '/checkout/') {
      setCurrentPage('checkout');
      return;
    }

    if (path === '/track-order' || path === '/track-order/' || path === '/track') {
      setCurrentPage('track-order');
      return;
    }

    if (path === '/shop' || path === '/shop-all' || path === '/shop/') {
      setCurrentPage('shop-all');
      return;
    }

    if (path === '/supplements' || path === '/supplements/') {
      setCurrentPage('supplements');
      return;
    }

    if (path === '/bundles' || path === '/bundles/') {
      setCurrentPage('bundles');
      return;
    }

    if (path === '/skincare') {
      setCurrentPage('skincare');
      return;
    }

    if (path === '/haircare') {
      setCurrentPage('haircare');
      return;
    }

    if (path === '/education-toys') {
      setCurrentPage('education-toys');
      return;
    }

    if (path === '/blog') {
      setCurrentPage('blog');
      return;
    }

    // Direct product landing routes: /products/:slug, /product/:slug, /item/:slug, or ?product=... / ?p=... / ?slug=... / ?id=...
    const productParam =
      searchParams.get('product') ||
      searchParams.get('p') ||
      searchParams.get('item') ||
      searchParams.get('slug') ||
      searchParams.get('id');

    const productMatch = path.match(/^\/(?:products|product|item|items)\/([^/?#]+)/);
    const rawSlugOrIdentifier = productMatch ? productMatch[1] : productParam;

    if (rawSlugOrIdentifier) {
      const decodedSlug = decodeURIComponent(rawSlugOrIdentifier).trim();
      const productCatalog = products.length > 0 ? products : FALLBACK_PRODUCTS;
      const found = findProductBySlugOrParam(productCatalog, decodedSlug);

      if (found) {
        setSelectedProduct(found);
        setIsProductNotFound(false);
        setCurrentPage('product-detail');
        return;
      } else {
        // Safe fallback - default to the flagship product so user never lands on empty page
        const fallbackProd = products[0] || FALLBACK_PRODUCTS[0];
        setSelectedProduct(fallbackProd);
        setIsProductNotFound(false);
        setCurrentPage('product-detail');
        return;
      }
    } else {
      setIsProductNotFound(false);
    }
  }, [products, isProductsLoading]);

  // Sync route on mount and when products load
  useEffect(() => {
    resolveRouteFromUrl();
  }, [resolveRouteFromUrl]);

  // Listen to browser popstate (Back/Forward navigation)
  useEffect(() => {
    const handlePopState = () => {
      resolveRouteFromUrl();
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [resolveRouteFromUrl]);

  // Track page view event on change & update SEO meta tags
  useEffect(() => {
    trackPageView(currentPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Dynamic Title, Description, Canonical, Open Graph, and Structured Data for SEO
    try {
      if (currentPage === 'product-detail' && selectedProduct) {
        const slug = getProductSlug(selectedProduct);
        updatePageSeo({
          title: `${selectedProduct.title} | Health Store Pakistan`,
          description: `Buy ${selectedProduct.title} online in Pakistan at Health Store. ${selectedProduct.shortDescription || 'Original MalRich weight gain nutrition with vital vitamins, minerals, and milk protein.'} Fast Cash on Delivery across Pakistan.`,
          canonicalPath: `/products/${slug}`,
          isPrivate: false,
          ogType: 'product',
          product: selectedProduct,
        });
      } else if (currentPage === 'shop-all') {
        updatePageSeo({
          title: 'Shop All Weight Gain & Nutrition Products | Health Store Pakistan',
          description: 'Browse all MalRich healthy weight gain powders and nutritional supplements in Pakistan. Order online with Cash on Delivery and free shipping above Rs. 2,000.',
          canonicalPath: '/shop-all',
          isPrivate: false,
        });
      } else if (currentPage === 'supplements') {
        updatePageSeo({
          title: 'Nutrition Supplements & Health Products | Health Store Pakistan',
          description: 'Explore premium dietary supplements, vitamins, and minerals in Pakistan. Halal nutrition supplements for daily health and energy at Health Store.',
          canonicalPath: '/supplements',
          isPrivate: false,
        });
      } else if (currentPage === 'bundles') {
        updatePageSeo({
          title: 'Weight Gain Bundle Deals & Value Packs | Health Store Pakistan',
          description: 'Save big on MalRich weight gain value packs and bundle deals in Pakistan. Buy 2 Get 1 Free and family bundles with Free Cash on Delivery.',
          canonicalPath: '/bundles',
          isPrivate: false,
        });
      } else if (currentPage === 'track-order') {
        updatePageSeo({
          title: 'Track Order Status | Health Store Pakistan',
          description: 'Track your Health Store Pakistan order in real-time. Check courier delivery status and dispatch information for Cash on Delivery orders.',
          canonicalPath: '/track-order',
          isPrivate: false,
        });
      } else if (currentPage === 'blog') {
        updatePageSeo({
          title: 'Health, Nutrition & Weight Gain Blog | Health Store Pakistan',
          description: 'Read expert tips on healthy weight gain, diet plans, nutrition, vitamins, and bodybuilding supplements in Pakistan from Health Store.',
          canonicalPath: '/blog',
          isPrivate: false,
        });
      } else if (currentPage === 'skincare') {
        updatePageSeo({
          title: 'Skincare & Health Products | Health Store Pakistan',
          description: 'Nourishing skincare and daily health wellness products in Pakistan with Cash on Delivery at Health Store.',
          canonicalPath: '/skincare',
          isPrivate: false,
        });
      } else if (currentPage === 'haircare') {
        updatePageSeo({
          title: 'Haircare & Nutrition Products | Health Store Pakistan',
          description: 'Premium haircare and vitamin supplements for healthy hair growth in Pakistan with Cash on Delivery.',
          canonicalPath: '/haircare',
          isPrivate: false,
        });
      } else if (currentPage === 'education-toys') {
        updatePageSeo({
          title: 'Educational Toys & Kids Nutrition | Health Store Pakistan',
          description: 'Kids educational developmental toys and nutritional growth supplements in Pakistan with Cash on Delivery.',
          canonicalPath: '/education-toys',
          isPrivate: false,
        });
      } else if (currentPage === 'checkout') {
        updatePageSeo({
          title: 'Checkout - Fast Cash on Delivery | Health Store Pakistan',
          description: 'Complete your Health Store order with fast Cash on Delivery across Pakistan.',
          canonicalPath: '/checkout',
          isPrivate: true,
        });
      } else if (currentPage === 'admin') {
        updatePageSeo({
          title: 'Admin Dashboard | Health Store Pakistan',
          description: 'Health Store Admin control panel.',
          canonicalPath: '/admin',
          isPrivate: true,
        });
      } else {
        // Default / Home
        updatePageSeo({
          title: 'Health Store | Weight Gain & Nutrition Products in Pakistan',
          description: 'Shop weight gain and nutrition products online in Pakistan. Buy MalRich weight gain products and health supplements with Cash on Delivery.',
          canonicalPath: '/',
          isPrivate: false,
        });
      }
    } catch {
      // ignore
    }
  }, [currentPage, selectedProduct]);

  // Handlers
  const handleNavigate = (page: string) => {
    const targetRoute = page as PageRoute;
    setCurrentPage(targetRoute);
    setIsSearchOpen(false);
    setIsAccountOpen(false);
    setIsMobileMenuOpen(false);
    setActivePolicy(null);
    setIsProductNotFound(false);

    // Update browser history URL
    try {
      const urlMap: Record<string, string> = {
        home: '/',
        'shop-all': '/shop-all',
        supplements: '/supplements',
        bundles: '/bundles',
        checkout: '/checkout',
        'track-order': '/track-order',
        blog: '/blog',
        skincare: '/skincare',
        haircare: '/haircare',
        'education-toys': '/education-toys',
        admin: '/admin',
      };
      const newPath = urlMap[targetRoute] || `/${targetRoute}`;
      window.history.pushState({}, '', newPath);
    } catch {
      // ignore
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSelectProduct = (product: Product) => {
    setSelectedProduct(product);
    setIsProductNotFound(false);
    setCurrentPage('product-detail');

    try {
      const productSlug = getProductSlug(product);
      // Retain search parameters if any
      const search = window.location.search;
      window.history.pushState({}, '', `/products/${productSlug}${search}`);
    } catch {
      // ignore
    }
  };

  const handleTrackOrderWithNumber = (orderNum: string) => {
    setTrackOrderNumber(orderNum);
    setCurrentPage('track-order');
    try {
      window.history.pushState({}, '', `/track-order?order=${encodeURIComponent(orderNum)}`);
    } catch {
      // ignore
    }
  };

  const activeProduct = selectedProduct || products[0] || ({} as Product);

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'home':
        return (
          <HomePage
            onSelectProduct={handleSelectProduct}
            onNavigate={handleNavigate}
          />
        );

      case 'shop-all':
        return (
          <ShopAllPage
            categoryTitle="Shop All Products"
            onSelectProduct={handleSelectProduct}
            onNavigateHome={() => handleNavigate('home')}
          />
        );

      case 'supplements':
        return (
          <ShopAllPage
            categoryTitle="Dietary Supplements"
            categoryFilter="Supplements"
            onSelectProduct={handleSelectProduct}
            onNavigateHome={() => handleNavigate('home')}
          />
        );

      case 'bundles':
        return (
          <GenericCollectionPage
            title="Buy More Save More (Bundles)"
            subtitle="Exclusive multi-pack deals on genuine MalRich nutrition powder with extra savings and Free Shipping."
            categoryFilter="Bundles"
            onSelectProduct={handleSelectProduct}
            onNavigateHome={() => handleNavigate('home')}
            onShopAll={() => handleNavigate('shop-all')}
          />
        );

      case 'product-detail':
        return (
          <ProductDetailPage
            initialProduct={activeProduct}
            isNotFound={isProductNotFound}
            onNavigateHome={() => handleNavigate('home')}
            onNavigateShop={() => handleNavigate('shop-all')}
            onProceedToCheckout={() => handleNavigate('checkout')}
            onTrackOrder={handleTrackOrderWithNumber}
          />
        );

      case 'checkout':
        return (
          <CheckoutPage
            onNavigateHome={() => handleNavigate('home')}
            onNavigateShop={() => handleNavigate('shop-all')}
            onTrackOrder={handleTrackOrderWithNumber}
          />
        );

      case 'track-order':
        return (
          <TrackOrderPage
            initialOrderNumber={trackOrderNumber}
            onNavigateShop={() => handleNavigate('shop-all')}
            onNavigateHome={() => handleNavigate('home')}
          />
        );

      case 'blog':
        return (
          <BlogPage
            onNavigateHome={() => handleNavigate('home')}
            onShopProducts={() => handleNavigate('shop-all')}
          />
        );

      case 'skincare':
        return (
          <GenericCollectionPage
            title="Skincare & Wellness"
            subtitle="Discover clean personal care and dermal hydration essentials curated by Health Store."
            onSelectProduct={handleSelectProduct}
            onNavigateHome={() => handleNavigate('home')}
            onShopAll={() => handleNavigate('shop-all')}
          />
        );

      case 'haircare':
        return (
          <GenericCollectionPage
            title="Hair Care"
            subtitle="Nourishing botanical formulas and fortifying haircare vitamins for stronger, healthier roots."
            onSelectProduct={handleSelectProduct}
            onNavigateHome={() => handleNavigate('home')}
            onShopAll={() => handleNavigate('shop-all')}
          />
        );

      case 'education-toys':
        return (
          <GenericCollectionPage
            title="Education & Toys"
            subtitle="Brain stimulation learning kits and developmental activities for growing children."
            onSelectProduct={handleSelectProduct}
            onNavigateHome={() => handleNavigate('home')}
            onShopAll={() => handleNavigate('shop-all')}
          />
        );

      case 'admin':
        return (
          <AdminPage
            onNavigateHome={() => handleNavigate('home')}
            onNavigateShop={() => handleNavigate('shop-all')}
            onSelectProduct={handleSelectProduct}
          />
        );

      default:
        return (
          <HomePage
            onSelectProduct={handleSelectProduct}
            onNavigate={handleNavigate}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-white text-[#172554] flex flex-col font-sans antialiased selection:bg-[#191975] selection:text-white">
      
      {/* 1. Top Announcement Bar */}
      <AnnouncementBar />

      {/* 2. Main Store Header */}
      <Header
        activeNav={currentPage}
        onNavigate={handleNavigate}
        onOpenSearch={() => setIsSearchOpen(true)}
        onOpenAccount={() => setIsAccountOpen(true)}
      />

      {/* 3. Promo / Trust Indicator Bar (Shown on Home and Shop pages) */}
      {(currentPage === 'home' || currentPage === 'shop-all' || currentPage === 'supplements') && (
        <PromoTrustBar />
      )}

      {/* 4. Main Page View Content */}
      <main className="flex-1 w-full">
        <Suspense
          fallback={
            <div className="w-full min-h-[50vh] flex items-center justify-center">
              <div className="w-8 h-8 border-3 border-[#191975] border-t-transparent rounded-full animate-spin" />
            </div>
          }
        >
          {renderCurrentPage()}
        </Suspense>
      </main>

      {/* 5. Store Footer */}
      <Footer
        onNavigate={handleNavigate}
        onOpenPolicyModal={(policy) => setActivePolicy(policy)}
      />

      {/* 6. Floating WhatsApp Contact Button */}
      <WhatsAppButton />

      {/* 7. Slide-over Cart Drawer */}
      <CartDrawer
        onNavigateToCheckout={() => {
          closeCart();
          handleNavigate('checkout');
        }}
        onNavigateToShop={() => closeCart()}
      />

      {/* 8. Search Overlay Modal (Lazy) */}
      <Suspense fallback={null}>
        {isSearchOpen && (
          <SearchModal
            isOpen={isSearchOpen}
            onClose={() => setIsSearchOpen(false)}
            onSelectProduct={(product) => {
              setIsSearchOpen(false);
              handleSelectProduct(product);
            }}
          />
        )}
      </Suspense>

      {/* 9. Account & Order History Modal (Lazy) */}
      <Suspense fallback={null}>
        {isAccountOpen && (
          <AccountModal
            isOpen={isAccountOpen}
            onClose={() => setIsAccountOpen(false)}
            onTrackOrder={(orderNumber) => {
              setIsAccountOpen(false);
              handleTrackOrderWithNumber(orderNumber);
            }}
            onNavigateToAdmin={() => {
              setIsAccountOpen(false);
              handleNavigate('admin');
            }}
          />
        )}
      </Suspense>

      {/* 10. Policy & Terms Modal (Lazy) */}
      <Suspense fallback={null}>
        {activePolicy && (
          <PolicyModal
            policyType={activePolicy}
            onClose={() => setActivePolicy(null)}
          />
        )}
      </Suspense>

      {/* 11. Quick 1-Click Order Modal (Instant COD checkout) */}
      <Suspense fallback={null}>
        {quickOrderProduct && (
          <QuickOrderModal
            product={quickOrderProduct}
            isOpen={Boolean(quickOrderProduct)}
            onClose={() => setQuickOrderProduct(null)}
            onTrackOrder={(orderNumber) => {
              setQuickOrderProduct(null);
              handleTrackOrderWithNumber(orderNumber);
            }}
          />
        )}
      </Suspense>

      {/* 12. Mobile Navigation Menu Drawer */}
      <MobileMenu
        isOpen={isMobileMenuOpen}
        activeNav={currentPage}
        onClose={() => setIsMobileMenuOpen(false)}
        onNavigate={(page) => {
          setIsMobileMenuOpen(false);
          handleNavigate(page);
        }}
      />

    </div>
  );
};

export default function App() {
  return (
    <StoreSettingsProvider>
      <ProductsProvider>
        <ReviewsProvider>
          <HeroProvider>
            <CartProvider>
              <MainAppContent />
            </CartProvider>
          </HeroProvider>
        </ReviewsProvider>
      </ProductsProvider>
    </StoreSettingsProvider>
  );
}
